import React, { useState, useEffect, useContext, useRef } from "react";
import Swal from "sweetalert2";
import "../../../assets/css/gcstudent/review/StudentReview.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import { Badge } from "react-bootstrap";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

const ratingLabels = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];

/* ── Interactive Star Rating ── */
const StarRating = ({ rating, hovered, onRate, onHover, onLeave }) => (
  <div className="gcStudent-sr-stars" onMouseLeave={onLeave}>
    {[1, 2, 3, 4, 5].map((star) => {
      const filled = star <= (hovered || rating);
      return (
        <button
          key={star}
          type="button"
          className={`gcStudent-sr-star ${filled ? "gcStudent-sr-star--filled" : ""}`}
          onClick={() => onRate(star)}
          onMouseEnter={() => onHover(star)}
          aria-label={`Rate ${star} star`}
        >
          <svg
            viewBox="0 0 24 24"
            fill={filled ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="1.6"
            width="28"
            height="28"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
      );
    })}
    {(hovered || rating) > 0 && (
      <span className="gcStudent-sr-rating-label">
        {ratingLabels[hovered || rating]}
      </span>
    )}
  </div>
);

/* ── Display Stars (read-only) ── */
const DisplayStars = ({ rating }) => (
  <div className="gcStudent-sr-display-stars">
    {[1, 2, 3, 4, 5].map((s) => (
      <svg
        key={s}
        viewBox="0 0 24 24"
        fill={s <= rating ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.6"
        width="16"
        height="16"
        className={
          s <= rating
            ? "gcStudent-sr-display-star--filled"
            : "gcStudent-sr-display-star--empty"
        }
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ))}
  </div>
);

/* ══════════════════════════════════
   Main Component
══════════════════════════════════ */
const ITEMS_PER_PAGE = 5; // yahan se page size control hoti hai

const StudentReviewPage = () => {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false); // initial API fetch loading
  const [viewLoading, setViewLoading] = useState(false);       // page switch loading (sirf visible reviews k liye)
  const [currentPage, setCurrentPage] = useState(1); // pagination state

  // Pehli render (initial mount) par view-loading trigger na ho, is liye ref se track kar rahe hain
  const isFirstRender = useRef(true);

  // Step 1 & Step 2: Extract from AppContext
  const { baseUrl, loggedInUser, authToken } = useContext(AppContext);
  const studentId = loggedInUser?.id;

  const loadAllReviews = () => {
    if (!studentId) return;

    setReviewsLoading(true);

    const formData = new FormData();
    formData.append("lbstudent_id", studentId);

    axios
      .post(`${baseUrl}/student/reviews/loadAllReviews`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success) {
          setReviews(res.data.reviews);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setReviewsLoading(false);
      });
  };

  // Step 5 & Context check in useEffect
  useEffect(() => {
    if (studentId) {
      loadAllReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  /* ── Pagination slice (sirf current page ki reviews list mein jayengi) ── */
  const paginatedReviews = reviews.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ── Naya review submit hone ya list refresh hone par page 1 pe reset ── */
  useEffect(() => {
    setCurrentPage(1);
  }, [reviews.length]);

  /* ── Sirf visible reviews ki loading dikhayein — jab pagination se
     dusre page par jayen (e.g. page 1 k 5 reviews dikhane k baad page 2
     pe jayen tu sirf 6-10 wale reviews ki loading chale). Poori list
     ek sath "pop" nahi hogi. ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
  }, [currentPage]);

  const submitReview = () => {
    if (!studentId) {
      Swal.fire({
        icon: "error",
        title: "Session Expired",
        text: "Please log in again.",
        confirmButtonColor: "#213b52",
      });
      return;
    }

    if (!rating || !reviewText.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please provide both a rating and review text.",
        confirmButtonColor: "#213b52",
      });
      return;
    }

    setSubmitting(true);

    // Step 3 & 4: Append studentId and use authToken from Context
    const formData = new FormData();
    formData.append("lbstudent_id", studentId);
    formData.append("rating", rating);
    formData.append("review", reviewText.trim());

    axios
      .post(`${baseUrl}/student/reviews/submitReview`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        console.log("log from the submit review response", res.data);
        setSubmitting(false);
        if (res.data.success === true) {
          setRating(0);
          setReviewText("");
          setSubmitted(true);
          loadAllReviews();

          Swal.fire({
            icon: "success",
            title: "Review Submitted!",
            text: "Your review will appear on the home page.",
            confirmButtonColor: "#213b52",
          });
          setTimeout(() => setSubmitted(false), 3000);
        } else {
          Swal.fire({
            icon: "error",
            title: "Failed",
            text: res.data.message || "Something went wrong.",
            confirmButtonColor: "#213b52",
          });
        }
      })
      .catch((err) => {
        setSubmitting(false);
        console.error(err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Something went wrong. Please try again.",
          confirmButtonColor: "#213b52",
        });
      });
  };

  return (
    <div className="gcStudent-sr-page">
      {/* ── Page Header ── */}
      <h2 className="gcStudent-sr-heading">Write a Review</h2>
      <p className="gcStudent-sr-subheading">
        Share your experience with our library platform. Your review will appear
        on the public site after admin approval.
      </p>

      {/* ── Form Card ── */}
      <div className="gcStudent-sr-card">
        {/* Stars */}
        <div className="gcStudent-sr-field">
          <label className="gcStudent-sr-label">Your Rating</label>
          <StarRating
            rating={rating}
            hovered={hovered}
            onRate={setRating}
            onHover={setHovered}
            onLeave={() => setHovered(0)}
          />
        </div>

        {/* Textarea */}
        <div className="gcStudent-sr-field">
          <label className="gcStudent-sr-label">Your Review</label>
          <textarea
            className="gcStudent-sr-textarea"
            placeholder="Tell us about your experience with the library..."
            rows={5}
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
          />
        </div>

        {/* Submit Button */}
        <button
          className={`gcStudent-sr-submit-btn ${
            submitted ? "gcStudent-sr-submit-btn--done" : ""
          }`}
          onClick={submitReview}
          disabled={submitting}
        >
          {submitting ? (
            <span className="gcStudent-sr-spinner" />
          ) : (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="16"
              height="16"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          )}
          {submitting
            ? "Submitting..."
            : submitted
            ? "Submitted!"
            : "Submit Review"}
        </button>
      </div>

      {/* ── Past Reviews ── */}
      <div className="gcStudent-sr-past-section">
        <h3 className="gcStudent-sr-past-heading">Your Past Reviews</h3>

        {/* ── Initial Loading (poora page pehli baar load ho raha hai) ── */}
        {reviewsLoading && <Loader message="Reviews loading, Please wait..." />}

        {!reviewsLoading && reviews.length === 0 && (
          <p className="gcStudent-sr-no-reviews">No reviews submitted yet.</p>
        )}

        {!reviewsLoading && reviews.length > 0 && (
          <>
            <div className="gcStudent-sr-past-list">
              {viewLoading ? (
                /* ── Page switch loading — sirf is section ki, baaki page affect nahi hota ── */
                <Loader message="Loading reviews..." />
              ) : (
                paginatedReviews.map((rev, i) => (
                  <div
                    key={rev.id}
                    className="gcStudent-sr-past-item"
                    style={{ animationDelay: `${i * 0.08}s` }}
                  >
                    <div className="gcStudent-sr-past-top d-flex justify-content-between">
                      <DisplayStars rating={Number(rev.rating)} />

                      <div className="text-end">
                        <div className="gcStudent-sr-past-date">
                          {new Date(rev.created_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </div>

                        <div className="mt-1">
                          <Badge
                            bg={rev.status === "Activate" ? "success" : "danger"}
                          >
                            {rev.status}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <p className="gcStudent-sr-past-text">{rev.review}</p>
                  </div>
                ))
              )}
            </div>

            {/* ── Pagination (view-loading k dauran bhi visible rehti hai) ── */}
            <Pagination
              currentPage={currentPage}
              totalItems={reviews.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default StudentReviewPage;